/**
 * Octopus IoT Platform - Notification Settings Modal
 * B2: Modal for configuring Email notification settings
 * 
 * NOTE: WhatsApp notifications are configured via Evolution API settings.
 * 
 * Reference: docs/OCTOPUS_ALERTCENTER_DESIGN_DOC.md
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { 
  Mail, 
  MessageSquare, 
  Send, 
  Loader2, 
  Eye, 
  EyeOff,
  CheckCircle,
  XCircle,
  ExternalLink,
  Info
} from 'lucide-react';
import alertsApi from '@/services/alertsApi';

const NotificationSettingsModal = ({ open, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  
  // Email settings
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [smtpHost, setSmtpHost] = useState('smtp.gmail.com');
  const [smtpPort, setSmtpPort] = useState(587);
  const [smtpEmail, setSmtpEmail] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [showEmailPassword, setShowEmailPassword] = useState(false);
  const [hasEmailPassword, setHasEmailPassword] = useState(false);

  useEffect(() => {
    if (open) {
      loadSettings();
    }
  }, [open]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await alertsApi.getNotificationSettings();
      const settings = response.data;
      
      setEmailEnabled(settings.email_enabled || false);
      setSmtpHost(settings.smtp_host || 'smtp.gmail.com');
      setSmtpPort(settings.smtp_port || 587);
      setSmtpEmail(settings.smtp_email || '');
      setHasEmailPassword(!!settings.smtp_email); // Assume password exists if email is set
    } catch (error) {
      console.error('Error loading notification settings:', error);
      toast.error('Erro ao carregar configurações de notificação');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const settings = {
        email_enabled: emailEnabled,
        smtp_host: smtpHost,
        smtp_port: parseInt(smtpPort, 10),
        smtp_email: smtpEmail || null
      };
      
      // Only include password if user entered a new value
      if (smtpPassword) {
        settings.smtp_password = smtpPassword;
      }
      
      await alertsApi.updateNotificationSettings(settings);
      toast.success('Configurações salvas com sucesso');
      onClose();
    } catch (error) {
      console.error('Error saving notification settings:', error);
      toast.error('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!emailEnabled || !smtpEmail) {
      toast.error('Configure e habilite o email primeiro');
      return;
    }
    
    setTestingEmail(true);
    try {
      const response = await alertsApi.testEmailNotification();
      toast.success(response.data.message || 'Email de teste enviado!');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao enviar email de teste';
      toast.error(msg);
    } finally {
      setTestingEmail(false);
    }
  };

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-lg">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="notification-settings-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" />
            Configuração de Notificações
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Email Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-500" />
                <span className="font-medium">Email (SMTP)</span>
              </div>
              <div className="flex items-center gap-2">
                {emailEnabled ? (
                  <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/30">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Ativo
                  </Badge>
                ) : (
                  <Badge variant="outline" className="bg-muted text-muted-foreground">
                    <XCircle className="w-3 h-3 mr-1" />
                    Inativo
                  </Badge>
                )}
                <Switch
                  checked={emailEnabled}
                  onCheckedChange={setEmailEnabled}
                  data-testid="email-enabled-switch"
                />
              </div>
            </div>

            <div className="grid gap-4 pl-7">
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="smtp-host" className="text-sm text-muted-foreground">Servidor SMTP</Label>
                  <Input
                    id="smtp-host"
                    value={smtpHost}
                    onChange={(e) => setSmtpHost(e.target.value)}
                    placeholder="smtp.gmail.com"
                    disabled={!emailEnabled}
                    data-testid="smtp-host-input"
                  />
                </div>
                <div>
                  <Label htmlFor="smtp-port" className="text-sm text-muted-foreground">Porta</Label>
                  <Input
                    id="smtp-port"
                    type="number"
                    value={smtpPort}
                    onChange={(e) => setSmtpPort(e.target.value)}
                    placeholder="587"
                    disabled={!emailEnabled}
                    data-testid="smtp-port-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="smtp-email" className="text-sm text-muted-foreground">Email / Gmail</Label>
                <Input
                  id="smtp-email"
                  type="email"
                  value={smtpEmail}
                  onChange={(e) => setSmtpEmail(e.target.value)}
                  placeholder="alertas@suaempresa.com"
                  disabled={!emailEnabled}
                  data-testid="smtp-email-input"
                />
              </div>

              <div>
                <Label htmlFor="smtp-password" className="text-sm text-muted-foreground">
                  App Password {hasEmailPassword && !smtpPassword && <span className="text-xs">(já configurado)</span>}
                </Label>
                <div className="relative">
                  <Input
                    id="smtp-password"
                    type={showEmailPassword ? 'text' : 'password'}
                    value={smtpPassword}
                    onChange={(e) => setSmtpPassword(e.target.value)}
                    placeholder={hasEmailPassword ? '••••••••••••' : 'Cole o App Password do Gmail'}
                    disabled={!emailEnabled}
                    data-testid="smtp-password-input"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1 h-7 w-7"
                    onClick={() => setShowEmailPassword(!showEmailPassword)}
                    disabled={!emailEnabled}
                  >
                    {showEmailPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={handleTestEmail}
                disabled={!emailEnabled || !smtpEmail || testingEmail}
                className="w-fit"
                data-testid="test-email-btn"
              >
                {testingEmail ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Testar Envio
              </Button>
            </div>
          </div>

          <Separator />

          {/* WhatsApp Info Section - Points to Evolution settings */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-green-500" />
              <span className="font-medium">WhatsApp (Evolution API)</span>
            </div>

            <Alert className="bg-muted/50 border-muted">
              <Info className="h-4 w-4" />
              <AlertDescription className="text-sm">
                As notificações por WhatsApp são configuradas na integração com Evolution API.
                <br />
                <Link
                  to="/settings"
                  onClick={onClose}
                  className="inline-flex items-center gap-1 text-primary hover:underline mt-2"
                >
                  Ir para Configurações do WhatsApp
                  <ExternalLink className="w-3 h-3" />
                </Link>
              </AlertDescription>
            </Alert>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose} data-testid="cancel-settings-btn">
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : null}
            Salvar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NotificationSettingsModal;
